// Developer: Ankit Gupta
/**
 * My profile: who I am in DataPilot, what I can reach, and what I have to spend.
 *
 * The e-mail address and the roles come from the sign-in and are shown, not edited. The name,
 * role, picture and the rest are the person's own. A profile can be copied in once from Slack
 * by an administrator; the person changes it here afterwards.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Input, Spin, Tag } from 'antd';
import { CameraOutlined, DeleteOutlined, SlackOutlined, SmileOutlined } from '@ant-design/icons';
import { api } from '@/api/client';
import { useT } from '@/i18n';
import { browserOrUiLocale } from '@/utils/format';
import { PageHeader } from '@/components/common/ui';
import { orionApi } from '@/pages/Chat/api';
import { squarePicture } from '@/pages/Chat/pictures';
import { getBalance } from '@/pages/Credits/api';
import { AvatarPicker } from './AvatarPicker';
import './profile.css';

export interface Profile {
  email: string;
  name: string;
  username: string;
  roles: string[];
  is_admin: boolean;
  display_name: string | null;
  title: string | null;
  organisation: string | null;
  phone: string | null;
  timezone: string | null;
  status_text: string | null;
  slack_user_id: string | null;
  avatar: string | null;
  source: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export const profileApi = {
  me: () => api.get<Profile>('/profile/me'),
  save: (body: Partial<Profile>) => api.put<Profile>('/profile/me', body),
};

/** The signed-in person's profile, for the page and for the picture in the top bar. */
export function useMyProfile() {
  return useQuery({ queryKey: ['profile-me'], queryFn: profileApi.me, staleTime: 5 * 60_000, retry: false });
}

// Shown in the viewer's language (account:profile.roles.*); the role codes themselves are data.
const KNOWN_ROLES = new Set(['datapilot_admin', 'datapilot_project_admin', 'datapilot_user']);
// Labels and placeholders live in account:profile.fields.<key>.
const FIELDS: { key: keyof Profile }[] = [
  { key: 'display_name' },
  { key: 'title' },
  { key: 'organisation' },
  { key: 'phone' },
  { key: 'timezone' },
  { key: 'status_text' },
];

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export default function ProfilePage() {
  const { t } = useT('account');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { data: me, isLoading } = useMyProfile();
  const apps = useQuery({ queryKey: ['orion-apps'], queryFn: () => orionApi.applications(), staleTime: 60_000 });
  const credits = useQuery({ queryKey: ['credits', 'balance'], queryFn: getBalance, staleTime: 60_000, retry: false });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const picker = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState(false);
  useEffect(() => { if (me) setDraft(Object.fromEntries(FIELDS.map((f) => [f.key, (me[f.key] as string | null) ?? '']))); }, [me]);
  const save = useMutation({
    mutationFn: (body: Partial<Profile>) => profileApi.save(body),
    onSuccess: (p) => { qc.setQueryData(['profile-me'], p); void qc.invalidateQueries({ queryKey: ['profile-face'] }); message.success(t('profile.saved')); },
    onError: (e: { response?: { data?: { detail?: string } } }) => message.error(e.response?.data?.detail ?? t('profile.saveFailed')),
  });
  if (isLoading || !me) return <div style={{ padding: 40 }}><Spin /></div>;
  const shownName = me.display_name || me.name;
  const changed = FIELDS.some((f) => (draft[f.key] ?? '') !== ((me[f.key] as string | null) ?? ''));
  const choose = async (file: File | undefined) => {
    if (!file) return;
    try {
      save.mutate({ avatar: await squarePicture(file, 256) });
    } catch (e) {
      message.error((e as Error).message);
    }
  };
  const mine = (apps.data ?? []).filter((a) => a.my_role);
  return (
    <div className="profile">
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />
      <section className="profile-card profile-head">
        <div className="profile-photo">
          {me.avatar ? <img src={me.avatar} alt="" /> : <span>{initialsOf(shownName)}</span>}
        </div>
        <div className="profile-who">
          <h2>{shownName}</h2>
          <div className="profile-sub">{[me.title, me.organisation].filter(Boolean).join(' · ') || t('profile.addRole')}</div>
          <div className="profile-sub">{me.email}</div>
          {me.status_text && <div className="profile-status">{me.status_text}</div>}
          <div className="profile-tags">
            {me.roles.map((r) => <Tag key={r} color={r === 'datapilot_admin' ? 'purple' : 'default'}>{KNOWN_ROLES.has(r) ? t(`profile.roles.${r}`) : r.replace(/^datapilot_/, '').replace(/_/g, ' ')}</Tag>)}
            {me.slack_user_id && <Tag icon={<SlackOutlined />}>{t('profile.slackTag', { id: me.slack_user_id })}</Tag>}
          </div>
        </div>
        <div className="profile-photo-actions">
          <input ref={picker} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = ''; }} />
          <Button icon={<CameraOutlined />} loading={save.isPending && !picking} onClick={() => picker.current?.click()}>{me.avatar ? t('profile.changePhoto') : t('profile.addPhoto')}</Button>
          <Button icon={<SmileOutlined />} onClick={() => setPicking(true)}>{t('profile.avatar.choose')}</Button>
          {me.avatar && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => save.mutate({ avatar: '' })}>{t('profile.remove')}</Button>}
        </div>
      </section>

      <AvatarPicker
        open={picking}
        initials={initialsOf(shownName)}
        saving={save.isPending}
        onClose={() => setPicking(false)}
        onPick={(avatar) => save.mutate({ avatar }, { onSuccess: () => setPicking(false) })}
      />

      {me.source === 'SLACK' && (
        <Alert type="info" showIcon message={t('profile.copiedFromSlack')} description={t('profile.copiedFromSlackBody')} />
      )}

      <div className="profile-grid">
        <section className="profile-card">
          <h3>{t('profile.aboutYou')}</h3>
          <div className="profile-form">
            {FIELDS.map((f) => (
              <label key={f.key}>
                <span>{t(`profile.fields.${f.key}.label`)}</span>
                <Input value={draft[f.key] ?? ''} placeholder={t(`profile.fields.${f.key}.placeholder`)} maxLength={f.key === 'status_text' ? 200 : 160} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} onPressEnter={() => changed && save.mutate(draft as Partial<Profile>)} />
              </label>
            ))}
            <label><span>{t('profile.email')}</span><Input value={me.email} disabled /></label>
          </div>
          <div className="profile-actions">
            <Button type="primary" disabled={!changed} loading={save.isPending} onClick={() => save.mutate(draft as Partial<Profile>)}>{t('profile.save')}</Button>
            <span className="profile-note">{t('profile.signInNote')}</span>
          </div>
        </section>

        <div className="profile-side">
          <section className="profile-card">
            <h3>{t('profile.orionTitle')}</h3>
            {apps.isLoading ? <Spin size="small" /> : mine.length === 0 ? (
              <p className="profile-note">{t('profile.noApplication')} <Link to="/orion/applications">{t('profile.askForAccess')}</Link>.</p>
            ) : (
              <ul className="profile-list">
                {mine.map((a) => (
                  <li key={a.code}>
                    <span>{a.name}</span>
                    <Tag color={a.my_role === 'owner' ? 'purple' : a.my_role === 'consultant' ? 'cyan' : 'default'}>{a.my_role_label}</Tag>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/orion/applications" className="profile-link">{t('profile.applicationsLink')}</Link>
          </section>
          <section className="profile-card">
            <h3>{t('profile.creditsTitle')}</h3>
            {credits.data ? (
              <>
                <div className="profile-big">{credits.data.balance_credits.toLocaleString(browserOrUiLocale())} <small>{t('profile.creditsWorth', { amount: credits.data.balance_usd.toLocaleString(browserOrUiLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}</small></div>
                <p className="profile-note">{credits.data.plan ? t('profile.planLine', { plan: credits.data.plan.name, credits: credits.data.plan.monthly_credits.toLocaleString(browserOrUiLocale()) }) : t('profile.noPlan')} {t('profile.spentThisMonth', { credits: credits.data.spent_this_month.toLocaleString(browserOrUiLocale()) })}</p>
              </>
            ) : <p className="profile-note">{t('profile.balanceElsewhere')}</p>}
            <Link to="/credits" className="profile-link">{t('profile.creditsLink')}</Link>
          </section>
        </div>
      </div>
    </div>
  );
}
